import type {
  PlanWindow,
  SubscriptionFamily,
  UsageValue,
} from "../../lib/types";

export const KEPT_OFF_TIP =
  "Tokens condense removed before they reached your provider, so they never counted against your plan's limits.";

export const STRETCH_TIP =
  "Tokens condense removes never reach your provider, so they never count against your plan's usage limits, such as its 5-hour and weekly windows. Stretch is how much more work fits in each window. Providers don't publish those limits, so stretch weighs tokens at API prices.";

export const API_SAVED_TIP =
  "Input cost saved on per-token API traffic, including cache reads and writes. The savings percentage is money saved divided by input spend plus money saved. Output costs and subscription traffic are excluded.";

export const OTHER_TIP =
  "Requests on a credential condense can't match to a Claude or ChatGPT plan or to an API key, such as another tool's login or a gateway's token. How they're billed is unknown, so they're shown in tokens, not dollars.";

export const API_BILL_TIP =
  "Input spend on per-token API traffic, including cache reads and writes, plus condense's fee. Output costs are excluded. Percent saved uses input spend before compression: input spend plus money saved. Net saved is what you keep after the fee.";

export type SavedTileMode = "loading" | "money" | "stretch" | "legacy";

/** Dollars only for per-token traffic; subscription-only users get plan
 * stretch. `undefined` is still loading, `null` failed to load. */
export function savedTileMode(v: UsageValue | null | undefined): SavedTileMode {
  if (v === undefined) return "loading";
  if (v?.api) return "money";
  if (v?.subscription) return "stretch";
  return "legacy";
}

const PLAN_NAMES: Record<SubscriptionFamily["family"], string> = {
  claude_code: "Claude",
  codex: "ChatGPT",
};

export function planName(family: SubscriptionFamily["family"]): string {
  return PLAN_NAMES[family] ?? "provider";
}

/** "Claude plan", "Claude and ChatGPT plans". */
export function plansPhrase(families: SubscriptionFamily[]): string {
  const names = [...new Set(families.map((f) => planName(f.family)))];
  if (names.length < 2) return `${names[0] ?? "provider"} plan`;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]} plans`;
}

/** "+58%", or null when there is no stretch worth showing. */
export function stretchLabel(
  stretch: number | null | undefined,
): string | null {
  if (stretch == null || !Number.isFinite(stretch)) return null;
  const more = Math.round((stretch - 1) * 100);
  if (more < 5) return null;
  return stretch >= 3 ? `${Math.round(stretch)}×` : `+${more}%`;
}

export function shownFamilies(v: UsageValue): SubscriptionFamily[] {
  return (v.subscription?.families ?? []).filter(
    (f) => f.requests > 0 || f.tokens_kept_off > 0,
  );
}

/** "5-hour limit", "Weekly limit", from the window's length. */
export function windowLabel(minutes: number): string {
  if (minutes === 10080) return "Weekly limit";
  if (minutes === 1440) return "Daily limit";
  if (minutes % 1440 === 0) return `${minutes / 1440}-day limit`;
  if (minutes % 60 === 0) return `${minutes / 60}-hour limit`;
  return `${minutes}-minute limit`;
}

/** Standard Claude limits plus provider-reported windows, shortest first. */
export function planWindows(plan: {
  windows?: PlanWindow[];
  family?: string;
}): PlanWindow[] {
  const windows = [...(plan.windows ?? [])];
  if (plan.family === "claude_code") {
    for (const minutes of [300, 10080]) {
      if (!windows.some((w) => w.minutes === minutes)) {
        windows.push({
          minutes,
          used: null,
          without_condense: null,
          resets_at: null,
        });
      }
    }
  }
  return windows
    .filter((w) => w.minutes > 0)
    .sort((a, b) => a.minutes - b.minutes);
}

export function lastReported(window: PlanWindow, now: number): boolean {
  const reported = Date.parse(window.reported_at ?? "");
  const reset = Date.parse(window.resets_at ?? "");
  return (
    !Number.isFinite(reported) || now - reported > 5 * 60000 || reset <= now
  );
}

export function readingLabel(
  window: PlanWindow,
  now: number,
): string | undefined {
  if (window.used == null) return "Waiting for a provider reading";
  if (Date.parse(window.resets_at ?? "") <= now) {
    return "Window ended; waiting for a new provider reading";
  }
  const reported = Date.parse(window.reported_at ?? "");
  if (!Number.isFinite(reported)) return "Last reported by your provider";
  const minutes = Math.max(0, Math.floor((now - reported) / 60000));
  if (minutes < 1) return "Updated just now";
  if (minutes < 60) return `Last updated ${minutes}m ago`;
  if (minutes < 1440) return `Last updated ${Math.floor(minutes / 60)}h ago`;
  return `Last updated ${Math.floor(minutes / 1440)}d ago`;
}

/** "resets in 2h 14m", "resets Mon, Nov 4", or nothing once past. */
export function resetLabel(
  iso: string | null | undefined,
  now: number,
): string | undefined {
  if (!iso) return undefined;
  const at = Date.parse(iso);
  if (!Number.isFinite(at) || at <= now) return undefined;
  const mins = Math.round((at - now) / 60000);
  if (mins < 60) return `resets in ${mins}m`;
  if (mins < 24 * 60) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m ? `resets in ${h}h ${m}m` : `resets in ${h}h`;
  }
  return `resets ${new Date(at).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}`;
}
